import { call, put, select, spawn } from 'redux-saga/effects';

import {
  fetchVisitorCounts,
  fetchVisitorCountsForDrafts,
  getClonedFromJourney,
  getValidateMultiContent,
  validateWorkflow,
} from 'api/WorkflowsAPI';
import { TemplateId } from 'modules/domains';
import { addNegativeToastWithErrorId } from 'modules/toastContainer';
import { BackendNode, BackendWorkflow, ClonedFromJourney, NodeId } from 'modules/workflows/types';
import { getErrorResponse } from 'utils/xhr';
import { WorkflowType } from '../../enums';
import { getDefaultMetricsDateRange } from '../../utils/dataUtils';
import {
  FetchVisitorCountsResponseAction,
  FetchWorkflowResponseAction,
  FetchVisitorCountsRequestAction,
  actions,
} from '../actions';
import { getWorkflowState } from '../selectors';
import {
  getClonedNodes,
  getParsedNodeData,
  getUniqueClonedNodeIds,
  mapErrorsToNodeId,
} from './sagaUtils';
import { ValidateClonedJourneySagaError } from './types';

interface GetClonedFromJourneyResponse {
  data: {
    workflow: {
      clonedFromJourney: ClonedFromJourney;
      nodes: BackendNode[];
    };
  };
}

export function* validateClonedJourneySaga(
  workflow: BackendWorkflow,
  workflowWithClonedData: GetClonedFromJourneyResponse['data']['workflow'],
  nodeToTemplateErrorsMap: ReturnType<typeof mapErrorsToNodeId>,
) {
  const { validatedNodes, clonedNodes } = getClonedNodes(
    workflow,
    workflowWithClonedData,
    nodeToTemplateErrorsMap,
  );
  let nodeIds: NodeId[] = [];

  try {
    const wfState: ReturnType<typeof getWorkflowState> = yield select(getWorkflowState);
    const updatedWorkflow = getParsedNodeData({ ...workflow, nodes: clonedNodes }, wfState);
    yield call(validateWorkflow, updatedWorkflow);
    nodeIds = getUniqueClonedNodeIds(validatedNodes);
  } catch (error) {
    const details = (error as ValidateClonedJourneySagaError)?.response?.data?.details || [];
    nodeIds = getUniqueClonedNodeIds([...details, ...validatedNodes]);
  } finally {
    if (nodeIds.length) {
      yield put(actions.addNodeErrors(nodeIds));
      yield put(actions.updateClonedJourneyErrorNodes(nodeIds));
    }
    yield put(actions.updateIsAsyncLoading(false));
  }
}

export function* validateClonedTemplatesSaga(
  workflow: BackendWorkflow,
  workflowWithClonedData: GetClonedFromJourneyResponse['data']['workflow'],
) {
  const { nodes = [] } = workflow;
  const templateIds: Map<TemplateId, number> = new Map();
  let nodeToTemplateErrorsMap: ReturnType<typeof mapErrorsToNodeId> = {};
  try {
    nodes.forEach((node) => {
      if (node.nodeData?.templateId) templateIds.set(node.nodeData.templateId, node.id);
    });
    const { data } = yield call(getValidateMultiContent, Array.from(templateIds.keys()));
    const { errors = [] } = data?.validateMultiContent;

    if (errors?.length) {
      nodeToTemplateErrorsMap = mapErrorsToNodeId(errors, templateIds);
    }
  } catch (error) {
    const message = `Error validating campaigns.`;
    yield put(addNegativeToastWithErrorId(message, 'validateClonedTemplatesSaga'));
  } finally {
    yield spawn(
      validateClonedJourneySaga,
      workflow,
      workflowWithClonedData,
      nodeToTemplateErrorsMap,
    );
  }
}

export function* fetchClonedFromJourneySaga(workflow: BackendWorkflow) {
  try {
    const { data } = yield call(getClonedFromJourney, workflow.id, workflow.workflowType);
    const { clonedFromJourney } = data?.workflow;

    if (clonedFromJourney) {
      yield put(actions.fetchClonedFromJourneyResponse(clonedFromJourney));
      yield spawn(validateClonedTemplatesSaga, workflow, data?.workflow);
    } else {
      yield put(actions.updateIsAsyncLoading(false));
    }
  } catch (error) {
    yield put(actions.updateIsAsyncLoading(false));
    const err = getErrorResponse(error);
    yield put(actions.fetchClonedFromJourneyError(err));
    const message = `Error fetching cloned journey.`;
    yield put(addNegativeToastWithErrorId(message, 'fetchClonedFromJourneySaga'));
  }
}

export function* fetchVisitorCountsSaga(id: number, workflowType: WorkflowType) {
  const errorMessage = 'Oops... we’re having a hard time loading the number of users in each tile.';

  try {
    const visitorCounts: FetchVisitorCountsResponseAction['payload']['countsData'] = yield call(
      workflowType === WorkflowType.DRAFT ? fetchVisitorCountsForDrafts : fetchVisitorCounts,
      id,
    );

    yield put(actions.fetchVisitorCountsResponse(visitorCounts));
  } catch (error) {
    const err = getErrorResponse(error);
    yield put(actions.fetchVisitorCountsError(err));
    yield put(addNegativeToastWithErrorId(errorMessage, 'fetchVisitorCountsSaga'));
  }
}

export function* fetchVisitorCountsRequestSaga(action: FetchVisitorCountsRequestAction) {
  const { workflowId, workflowType } = action.payload;
  yield spawn(fetchVisitorCountsSaga, workflowId, workflowType);
}

export default function* v(action: FetchWorkflowResponseAction) {
  try {
    const { workflow, shouldFetchClonedFromJourney } = action.payload;
    const { workflowType, id, nodes } = workflow;

    if (shouldFetchClonedFromJourney) {
      yield spawn(fetchClonedFromJourneySaga, workflow);
    } else {
      yield put(actions.updateIsAsyncLoading(false));
    }
    yield spawn(fetchVisitorCountsSaga, id, workflowType as WorkflowType);

    // there are no send metrics to fetch if...
    // 1. the workflow is a draft
    // 2. there is only one node in the workflow, meaning that it's the start node
    if (workflowType === WorkflowType.DRAFT || nodes?.length === 1) return;

    const dateRange = getDefaultMetricsDateRange(workflow.createdAt as string);
    yield put(actions.fetchSendMetricsRequest(workflow, dateRange));
  } catch (error) {
    yield put(actions.updateIsAsyncLoading(false));
    yield put(
      addNegativeToastWithErrorId(
        'Error fetching workflow data. Please try again later.',
        'fetchAdditionalWorkflowDataSaga',
      ),
    );
  }
}
